import React, { useCallback, useState } from 'react';

import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { GetChatQuery, useRenameChatMutation } from '@/graphql';
import { useSnackbar } from 'notistack';

import styles from './EditGroup.module.scss';

export interface EditGroupProps {
	chat: GetChatQuery['getChat'];
	loadingChatChannel: boolean;
	loadChatChannel: () => Promise<void>;
	popDisplayState: () => void;
}

export const EditGroup: React.FC<EditGroupProps> = ({
	chat,
	loadingChatChannel,
	loadChatChannel,
	popDisplayState,
}) => {
	const MAX_GROUP_NAME_LENGTH = 80;

	const { mutateAsync: renameChat, isLoading } = useRenameChatMutation();

	const [groupName, setGroupName] = useState<string>(chat.name ?? '');

	const { enqueueSnackbar } = useSnackbar();

	const saveButtonDisabled =
		groupName === (chat.name ?? '') || isLoading || loadingChatChannel;

	const handleGroupName = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			if (e.target.value.length > MAX_GROUP_NAME_LENGTH) {
				return;
			} else {
				setGroupName(e.target.value);
			}
		},
		[],
	);

	const save = useCallback(async () => {
		try {
			await renameChat({
				chatId: chat.id,
				name: groupName === '' ? null : groupName,
			});
		} catch (e) {
			enqueueSnackbar(
				'Something went wrong while updating the group name',
				{
					variant: 'error',
				},
			);
			return;
		} finally {
			loadChatChannel();
			popDisplayState();
		}
	}, [
		chat.id,
		enqueueSnackbar,
		groupName,
		loadChatChannel,
		popDisplayState,
		renameChat,
	]);

	return (
		<>
			<section className={styles['edit-group']}>
				<div className={styles['edit-group__content']}>
					<FormInput
						id="group-name"
						name="group-name"
						value={groupName}
						onChange={handleGroupName}
						label="Group name"
						placeholder="e.g. Team One"
						endAdornment={
							<span className={styles['edit-group__adornment']}>
								{MAX_GROUP_NAME_LENGTH - groupName.length}
							</span>
						}
						disabled={isLoading}
						autoComplete="off"
						fullWidth
					/>
				</div>
			</section>
			<div className={styles['edit-group__action']}>
				<AsaphButton
					color="blue"
					label="Save"
					onClick={save}
					disabled={saveButtonDisabled}
					fullWidth
				/>
			</div>
		</>
	);
};
